import { useNotifications } from '../contexts/NotificationContext';

export class NotificationService {
  private static instance: NotificationService;
  private notificationContext: any = null;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  setContext(context: any) {
    this.notificationContext = context;
  }

  // Notificaciones para nuevos trabajos
  notifyNewJob(jobTitle: string, location: string) {
    this.addNotification({
      title: 'Nuevo trabajo disponible',
      message: `${jobTitle} en ${location}`,
      type: 'info',
      action: {
        type: 'view_job',
        payload: { jobTitle, location }
      }
    });
  }

  // Notificaciones de estado de trabajo
  notifyJobStatus(jobTitle: string, status: 'accepted' | 'in_progress' | 'completed' | 'cancelled') {
    const statusMessages = {
      accepted: 'Tu trabajo ha sido aceptado',
      in_progress: 'El trabajo está en progreso',
      completed: '¡Trabajo completado!',
      cancelled: 'El trabajo ha sido cancelado'
    };

    const statusTypes = {
      accepted: 'success',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'warning'
    } as const;

    this.addNotification({
      title: statusMessages[status],
      message: `"${jobTitle}" - ${statusMessages[status]}`,
      type: statusTypes[status],
      action: {
        type: 'view_job',
        payload: { jobTitle, status }
      }
    });
  }

  // Notificaciones de chat
  notifyNewMessage(senderName: string, message: string) {
    this.addNotification({
      title: `Nuevo mensaje de ${senderName}`,
      message: message.length > 50 ? `${message.substring(0, 50)}...` : message,
      type: 'info',
      action: {
        type: 'open_chat',
        payload: { senderName, message }
      }
    });
  }

  // Notificaciones de pago
  notifyPayment(amount: number, status: 'received' | 'sent') {
    const action = status === 'received' ? 'recibido' : 'enviado';
    this.addNotification({
      title: `Pago ${action}`,
      message: `$${amount.toFixed(2)} ha sido ${action} exitosamente`,
      type: 'success',
      action: {
        type: 'navigate',
        payload: { route: '/wallet' }
      }
    });
  }

  // Notificaciones de calificación
  notifyRating(rating: number, jobTitle: string) {
    const stars = '⭐'.repeat(rating);
    this.addNotification({
      title: 'Nueva calificación',
      message: `${stars} para "${jobTitle}"`,
      type: 'success',
      action: {
        type: 'navigate',
        payload: { route: '/profile' }
      }
    });
  }

  // Notificaciones de zona de alta demanda
  notifyHighDemandZone(zoneName: string) {
    this.addNotification({
      title: 'Zona de alta demanda',
      message: `${zoneName} tiene muchos trabajos disponibles`,
      type: 'info',
      action: {
        type: 'navigate',
        payload: { route: '/map', zone: zoneName }
      }
    });
  }

  // Notificaciones de error
  notifyError(message: string) {
    this.addNotification({
      title: 'Error',
      message,
      type: 'error'
    });
  }

  // Notificaciones de éxito
  notifySuccess(title: string, message: string) {
    this.addNotification({
      title,
      message,
      type: 'success'
    });
  }

  // Notificaciones de advertencia
  notifyWarning(title: string, message: string) {
    this.addNotification({
      title,
      message,
      type: 'warning'
    });
  }

  private addNotification(notification: any) {
    if (this.notificationContext && this.notificationContext.addNotification) {
      this.notificationContext.addNotification(notification);
      
      // Simular notificación push del navegador
      this.showBrowserNotification(notification);
    }
  }

  private showBrowserNotification(notification: any) {
    // Verificar si el navegador soporta notificaciones
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notification.id,
        requireInteraction: false,
        silent: false
      });
    }
  }

  // Solicitar permisos de notificación
  async requestNotificationPermission(): Promise<boolean> {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  // Simular notificaciones automáticas para demostración
  startDemoNotifications() {
    const demoNotifications = [
      {
        delay: 5000,
        notification: {
          title: 'Bienvenido a Iyire',
          message: '¡Tu cuenta ha sido creada exitosamente!',
          type: 'success' as const
        }
      },
      {
        delay: 15000,
        notification: {
          title: 'Nuevo trabajo disponible',
          message: 'Limpieza de casa en Palermo - $50',
          type: 'info' as const,
          action: {
            type: 'view_job',
            payload: { jobTitle: 'Limpieza de casa', location: 'Palermo' }
          }
        }
      },
      {
        delay: 25000,
        notification: {
          title: 'Nuevo mensaje',
          message: 'María: "¿Estás disponible para mañana?"',
          type: 'info' as const,
          action: {
            type: 'open_chat',
            payload: { senderName: 'María', message: '¿Estás disponible para mañana?' }
          }
        }
      }
    ];

    demoNotifications.forEach(({ delay, notification }) => {
      setTimeout(() => {
        this.addNotification(notification);
      }, delay);
    });
  }
}

// Hook personalizado para usar el servicio
export const useNotificationService = () => {
  const notificationContext = useNotifications();
  const service = NotificationService.getInstance();
  service.setContext(notificationContext);
  
  return service;
}; 